// Common
import { Component } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';

// Services
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { DragData, dragDataTypeAllowed, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { SheetFoldersFilters } from '@modules/sheets/types/sheet-folders-filters';
import { VirtualFolder } from '@modules/sheets/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';

// RxJs
import { take } from 'rxjs/operators';

// Components
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { withOrphansAtRoot } from '../../folders-tree';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';

@Component({
  selector: 'app-side-bar-sheet-folders-tree',
  templateUrl: './side-bar-sheet-folders-tree.component.html',
  styleUrls: ['./side-bar-sheet-folders-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarSheetFoldersTreeComponent extends BaseSidebarContainersTreeComponent<
  SheetFolder,
  Sheet,
  SheetFoldersFilters,
  VirtualFolder
> {
  selfDragDataTypes = [DragDataTypes.sheet, DragDataTypes.sheetFolder];
  containerDragDataType = DragDataTypes.sheetFolder;
  application = Application.sheets;
  treeStateKey = StateKey.sheetsSidebarSheetFoldersTree;

  public dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.sheetFolder &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type);

  constructor(
    private sheetFoldersService: SheetFoldersService,
    private sheetsService: SheetsService,
  ) {
    super(sheetFoldersService);
  }

  /** A folder whose parent isn't listed (in Trash or archived) is drawn at the root rather than nowhere. */
  protected override onItemsReceived(): void {
    this.items = withOrphansAtRoot(this.items);
  }

  /**
   * Actions
   */

  public handleMove(dragData: DragData, item: SheetFolder) {
    const message = 'Successfully moved to ' + item.title;

    if (dragData.type === DragDataTypes.sheet) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.sheetsService, dragData.data, () =>
        this.sheetsService.bunchUpdate({ ids }, { sheetFolderId: item.id }, { message }),
      );
    } else if (dragData.type === DragDataTypes.sheetFolder) {
      this.sheetFoldersService.bunchUpdate({ ids: this.getIds(dragData.data) }, { parentId: item.id }, { message });
    }
  }

  public handleDuplicate(dragData: DragData, sheetFolder: SheetFolder) {
    if (dragData.type === DragDataTypes.sheet) {
      this.sheetsService.duplicate(this.getIds(dragData.data), sheetFolder.id).pipe(take(1)).subscribe();
    } else if (dragData.type === DragDataTypes.sheetFolder) {
      this.sheetFoldersService.duplicate(this.getIds(dragData.data)).pipe(take(1)).subscribe();
    }
  }
}
